// Gamma-styled progress bar with color thresholds
interface ProgressBarProps {
  value: number | null | undefined;
  max?: number;
  /** Threshold color scheme. Default: 'standard' (green->amber->red as value rises) */
  colorScheme?: 'standard' | 'inverted' | 'fuel';
  showLabel?: boolean;
  height?: string;
}

function getBarColor(pct: number, scheme: 'standard' | 'inverted' | 'fuel'): string {
  if (scheme === 'inverted') {
    // Low = red, mid = amber, high = green (e.g., SOC, fuel)
    if (pct < 10) return 'bg-ems-red';
    if (pct < 20) return 'bg-ems-amber';
    return 'bg-ems-green';
  }
  if (scheme === 'fuel') {
    // Low = red, low-mid = amber, rest = ems-blue
    if (pct < 10) return 'bg-ems-red';
    if (pct < 15) return 'bg-ems-amber';
    return 'bg-ems-blue';
  }
  // standard: Low = green, high = amber, very high = red (e.g., engine load)
  if (pct > 95) return 'bg-ems-red';
  if (pct > 80) return 'bg-ems-amber';
  return 'bg-ems-green';
}

export function ProgressBar({
  value,
  max = 100,
  colorScheme = 'standard',
  showLabel = false,
  height = 'h-1',
}: ProgressBarProps) {
  const safeValue = value ?? 0;
  const pct = max > 0 ? Math.min(100, Math.max(0, (safeValue / max) * 100)) : 0;
  const barColor = getBarColor(pct, colorScheme);

  return (
    <div className="w-full flex items-center gap-2">
      <div className={`flex-1 ${height} rounded-full bg-ems-bg overflow-hidden`}>
        <div
          className={`${height} rounded-full ${barColor} transition-all duration-500 ease-out`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {showLabel && (
        <span className="text-[10px] font-mono text-ems-text-muted w-8 text-right flex-shrink-0">
          {safeValue.toFixed(0)}%
        </span>
      )}
    </div>
  );
}
